import cx from 'classnames';
import { Placement as PopperPlacement, PositioningStrategy } from '@popperjs/core';
import { CSSProperties, FC, HTMLAttributes, MouseEventHandler, ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PopoverPanelSection from './PopoverPanelSection';
import styles from './Popover.module.css';

const PANEL_GAP_PX = 8;
const POINTER_ANCHOR_GAP_PX = 4;

const NOTICE_POSITION_TO_PLACEMENT: Record<string, PopperPlacement> = {
  'top': 'top',
  'bottom': 'bottom',
  'right': 'right',
  'right-start': 'right-start',
  'right-end': 'right-end',
  'left': 'left',
  'top-left': 'top-start',
  'top-right': 'top-end',
  'bottom-left': 'bottom-start',
  'bottom-right': 'bottom-end',
};

const DEFAULT_FALLBACK_PLACEMENTS: Record<PopperPlacement, PopperPlacement[]> = {
  'top': ['bottom', 'right', 'left'],
  'bottom': ['top', 'right', 'left'],
  'left': ['right', 'top', 'bottom'],
  'right': ['left', 'top', 'bottom'],
  'top-start': ['bottom-start', 'bottom', 'right', 'left'],
  'top-end': ['bottom-end', 'bottom', 'left', 'right'],
  'bottom-start': ['top-start', 'top', 'right', 'left'],
  'bottom-end': ['top-end', 'top', 'left', 'right'],
  'left-start': ['right-start', 'right', 'top', 'bottom'],
  'left-end': ['right-end', 'right', 'bottom', 'top'],
  'right-start': ['left-start', 'left', 'top', 'bottom'],
  'right-end': ['left-end', 'left', 'bottom', 'top'],
  'auto': ['top', 'bottom', 'right', 'left'],
  'auto-start': ['top-start', 'bottom-start', 'right-start', 'left-start'],
  'auto-end': ['top-end', 'bottom-end', 'right-end', 'left-end'],
};

function getFallbackPlacements(noticePosition: string, fallbackPlacements?: string[]) {
  const placement = NOTICE_POSITION_TO_PLACEMENT[noticePosition];

  if (fallbackPlacements?.length) {
    return fallbackPlacements
      .map(fallbackPlacement => NOTICE_POSITION_TO_PLACEMENT[fallbackPlacement])
      .filter((resolvedPlacement): resolvedPlacement is PopperPlacement => Boolean(resolvedPlacement));
  }

  return DEFAULT_FALLBACK_PLACEMENTS[placement] ?? ['top'];
}

export function getSmallPopoverOptions({
  noticePosition,
  horizontalOffset,
  verticalOffset,
  disableAutoPosition,
  fallbackPlacements,
  hasPointer,
  positioningStrategy,
}: {
  noticePosition: string;
  horizontalOffset: number;
  verticalOffset: number;
  disableAutoPosition: boolean;
  fallbackPlacements?: string[];
  hasPointer: boolean;
  positioningStrategy: PositioningStrategy;
}) {
  return {
    placement: NOTICE_POSITION_TO_PLACEMENT[noticePosition],
    strategy: positioningStrategy,
    modifiers: [
      {
        name: 'eventListeners',
        options: {
          scroll: !disableAutoPosition,
          resize: !disableAutoPosition,
        },
      },
      {
        name: 'computeStyles',
        options: {
          adaptive: !disableAutoPosition,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [horizontalOffset, PANEL_GAP_PX + (hasPointer ? POINTER_ANCHOR_GAP_PX : 0) + verticalOffset],
        },
      },
      {
        name: 'flip',
        enabled: !disableAutoPosition,
        options: {
          padding: PANEL_GAP_PX,
          fallbackPlacements: getFallbackPlacements(noticePosition, fallbackPlacements),
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: PANEL_GAP_PX,
          altAxis: true,
          rootBoundary: 'viewport',
        },
      },
    ],
  };
}

interface PopoverAnchorProps {
  anchorRef?: (node: HTMLSpanElement | null) => void;
  className?: string;
  dataTestId: string;
  isOpen: boolean;
  anchorContent: ReactNode;
  onClick: VoidFunction;
  onMouseEnter?: VoidFunction;
  onMouseLeave?: VoidFunction;
}

export const PopoverAnchor: FC<PopoverAnchorProps> = ({
  anchorRef,
  className,
  dataTestId,
  isOpen,
  anchorContent,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) => (
  <span ref={anchorRef} className={cx(styles.popoverContainer, className)} data-testid={dataTestId} data-open={isOpen}>
    <section
      className={styles.popoverAnchor}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid={dataTestId + '-anchor'}
      data-open={isOpen}
    >
      {anchorContent}
    </section>
  </span>
);

interface MediumPopoverPortalProps {
  dataTestId: string;
  overlayingNoticeTestId: string;
  title?: string;
  noticeTitleProps?: HTMLAttributes<HTMLHeadingElement>;
  withCloseButton: boolean;
  headerClassName?: string;
  noticeContent: ReactNode;
  onClose: VoidFunction;
  onContentClick: MouseEventHandler<HTMLDivElement>;
  onBackdropClick: VoidFunction;
  usePortal: boolean;
}

export const MediumPopoverPortal: FC<MediumPopoverPortalProps> = ({
  dataTestId,
  overlayingNoticeTestId,
  title,
  noticeTitleProps,
  withCloseButton,
  headerClassName,
  noticeContent,
  onClose,
  onContentClick,
  onBackdropClick,
  usePortal,
}) => {
  const panelContent = (
    <>
      <div className={styles.backdrop} onClick={onBackdropClick} data-testid={dataTestId + '-backdrop'} />
      <div className={cx(styles.popoverPanel, styles['popoverPanel--M'])} data-testid={overlayingNoticeTestId}>
        <PopoverPanelSection
          title={title}
          noticeTitleProps={noticeTitleProps}
          withCloseButton={withCloseButton}
          headerClassName={headerClassName}
          titleTestId={dataTestId + '-title'}
          closeButtonTestId={dataTestId + '-close-button'}
          contentTestId={dataTestId + '-content'}
          noticeContent={noticeContent}
          onClose={onClose}
          onContentClick={onContentClick}
        />
      </div>
    </>
  );

  return usePortal ? createPortal(panelContent, document.body) : panelContent;
};

interface SmallPopoverPortalProps {
  panelRef: (node: HTMLDivElement | null) => void;
  popperStyle?: CSSProperties;
  panelAttributes: HTMLAttributes<HTMLDivElement>;
  hasPointer: boolean;
  isRaw: boolean;
  layer: 'default' | 'below-header';
  pointerClassName?: string;
  dataTestId: string;
  title?: string;
  noticeTitleProps?: HTMLAttributes<HTMLHeadingElement>;
  withCloseButton: boolean;
  headerClassName?: string;
  noticeContent: ReactNode;
  onClose: VoidFunction;
  onContentClick: MouseEventHandler<HTMLDivElement>;
  onMouseEnter?: VoidFunction;
  onMouseLeave?: VoidFunction;
  usePortal: boolean;
}

export const SmallPopoverPortal: FC<SmallPopoverPortalProps> = ({
  panelRef,
  popperStyle,
  panelAttributes,
  hasPointer,
  isRaw,
  layer,
  pointerClassName,
  dataTestId,
  title,
  noticeTitleProps,
  withCloseButton,
  headerClassName,
  noticeContent,
  onClose,
  onContentClick,
  onMouseEnter,
  onMouseLeave,
  usePortal,
}) => {
  const panelContent = (
    <div
      ref={panelRef}
      style={popperStyle}
      className={cx(
        styles.sPanel,
        hasPointer && styles.sPanelWithPointer,
        isRaw && styles.sPanelBare,
        !usePortal && layer === 'below-header' && styles.sPanelInlineBelowHeader,
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid={dataTestId + '-notice'}
      {...panelAttributes}
    >
      {hasPointer && <span className={cx(styles.pointer, pointerClassName)} data-testid={dataTestId + '-pointer'} />}
      <PopoverPanelSection
        title={title}
        noticeTitleProps={noticeTitleProps}
        withCloseButton={withCloseButton}
        headerClassName={headerClassName}
        useBareContent={isRaw}
        titleTestId={dataTestId + '-notice-title'}
        closeButtonTestId={dataTestId + '-notice-close-button'}
        contentTestId={dataTestId + '-notice-content'}
        noticeContent={noticeContent}
        onClose={onClose}
        onContentClick={onContentClick}
      />
    </div>
  );

  return usePortal ? createPortal(panelContent, document.body) : panelContent;
};

export function useOnCloseRef(onClose?: VoidFunction) {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  return onCloseRef;
}

export function useMediumPopoverBodyLock(size: 'M' | 'S', isOpen: boolean) {
  useEffect(() => {
    if (size !== 'M' || !isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [size, isOpen]);
}
